import { useState, useEffect, useCallback } from "react";
import {
  HelpCircle, ChevronDown, IndianRupee, X, Plus
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders custom fields dynamically based on field definitions.
 * @param {string} mode - "create" | "edit" | "detail"
 * @param {object} values - Current field values { field_key: value }
 * @param {function} onChange - (field_key, value) => void
 * @param {array} fields - Custom field definitions from API (optional, will fetch if not provided)
 */
export function CustomFieldsRenderer({ mode = "create", values = {}, onChange, fields: propFields }) {
  const [fields, setFields] = useState(propFields || []);
  const [loaded, setLoaded] = useState(!!propFields);

  useEffect(() => {
    if (propFields) { setFields(propFields); setLoaded(true); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/lead-fields`, { credentials: "include" });
        if (res.ok) { setFields(await res.json()); }
      } catch (e) { console.error("Failed to fetch custom fields", e); }
      finally { setLoaded(true); }
    })();
  }, [propFields]);

  if (!loaded) return null;

  const visibilityKey = mode === "create" ? "show_in_create" : mode === "edit" ? "show_in_edit" : "show_in_detail";
  const customFields = fields.filter(f => !f.is_system && f.is_active && f[visibilityKey]);

  if (customFields.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="custom-fields-section">
      <div className="flex items-center gap-2 pt-2 pb-1 border-t border-slate-200 dark:border-slate-700 mt-2">
        <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Custom Fields</span>
        <Badge variant="outline" className="text-[10px] h-4">{customFields.length}</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {customFields.map(field => (
          <CustomFieldInput
            key={field.field_id}
            field={field}
            value={values[field.field_key] ?? values[field.field_id] ?? ""}
            onChange={val => onChange(field.field_key, val)}
            readOnly={mode === "detail"}
          />
        ))}
      </div>
    </div>
  );
}

function CustomFieldInput({ field, value, onChange, readOnly }) {
  const { field_type, field_label, placeholder, help_text, is_required, options = [] } = field;

  const labelEl = (
    <Label className="text-xs font-medium flex items-center gap-1 mb-1">
      {field_label}
      {is_required && <span className="text-red-500">*</span>}
      {help_text && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild><HelpCircle className="w-3 h-3 text-slate-400 cursor-help" /></TooltipTrigger>
            <TooltipContent><p className="text-xs max-w-48">{help_text}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </Label>
  );

  if (readOnly) {
    return (
      <div data-testid={`custom-field-${field.field_key}`}>
        {labelEl}
        <p className="text-sm text-slate-700 dark:text-slate-300 py-1">
          {Array.isArray(value) ? value.join(", ") : (value || "-")}
        </p>
      </div>
    );
  }

  switch (field_type) {
    case "text":
    case "email":
    case "phone":
    case "url":
    case "number":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Input
            type={field_type === "number" ? "number" : field_type === "email" ? "email" : field_type === "url" ? "url" : field_type === "phone" ? "tel" : "text"}
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || `Enter ${field_label.toLowerCase()}...`}
            className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm"
            data-testid={`custom-input-${field.field_key}`}
          />
        </div>
      );

    case "textarea":
      return (
        <div className="sm:col-span-2" data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Textarea
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || `Enter ${field_label.toLowerCase()}...`}
            className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl text-sm resize-none min-h-16"
            data-testid={`custom-input-${field.field_key}`}
          />
        </div>
      );

    case "currency":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <div className="relative">
            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              type="number"
              value={value || ""}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder || "0"}
              className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm pl-7"
              data-testid={`custom-input-${field.field_key}`}
            />
          </div>
        </div>
      );

    case "date":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Input
            type="date"
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm"
            data-testid={`custom-input-${field.field_key}`}
          />
        </div>
      );

    case "time":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Input
            type="time"
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm"
            data-testid={`custom-input-${field.field_key}`}
          />
        </div>
      );

    case "dropdown":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm"
              data-testid={`custom-input-${field.field_key}`}>
              <SelectValue placeholder={placeholder || `Select ${field_label}...`} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "multiselect":
      const selected = Array.isArray(value) ? value : (value ? [value] : []);
      return (
        <div className="sm:col-span-2" data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {selected.map(s => (
              <Badge key={s} variant="secondary" className="gap-1 pr-1 text-xs">
                {s}
                <button onClick={() => onChange(selected.filter(v => v !== s))}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
          <Select value="" onValueChange={v => { if (!selected.includes(v)) onChange([...selected, v]); }}>
            <SelectTrigger className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm">
              <SelectValue placeholder={`Add ${field_label}...`} />
            </SelectTrigger>
            <SelectContent>
              {options.filter(o => !selected.includes(o)).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "radio":
      return (
        <div className="sm:col-span-2" data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <div className="flex flex-wrap gap-2">
            {options.map(opt => (
              <label key={opt} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-all ${
                value === opt ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700" : "border-slate-200 dark:border-slate-600 hover:border-slate-300"
              }`}>
                <input type="radio" name={field.field_key} value={opt} checked={value === opt}
                  onChange={() => onChange(opt)} className="sr-only" />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );

    case "checkbox":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={value === true || value === "true"}
              onChange={e => onChange(e.target.checked)}
              className="rounded border-slate-300" />
            {placeholder || field_label}
          </label>
        </div>
      );

    case "toggle":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <div className="flex items-center gap-2">
            <Switch checked={value === true || value === "true"}
              onCheckedChange={c => onChange(c)} />
            <span className="text-sm text-slate-500">{value ? "Yes" : "No"}</span>
          </div>
        </div>
      );

    case "file":
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Input type="file" onChange={e => { if (e.target.files?.[0]) onChange(e.target.files[0].name); }}
            className="bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 rounded-xl h-9 text-sm"
            data-testid={`custom-input-${field.field_key}`} />
        </div>
      );

    default:
      return (
        <div data-testid={`custom-field-${field.field_key}`}>
          {labelEl}
          <Input value={value || ""} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} className="bg-slate-50 rounded-xl h-9 text-sm" />
        </div>
      );
  }
}

/**
 * Hook to manage custom field values for a lead.
 * Returns [customValues, setCustomValue, saveCustomValues, loadCustomValues]
 */
export function useCustomFieldValues(leadId) {
  const [customValues, setCustomValues] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadCustomValues = useCallback(async () => {
    if (!leadId) return;
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/custom-values`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const valMap = {};
        data.forEach(v => { valMap[v.field_id] = v.field_value; });
        setCustomValues(valMap);
      }
    } catch (e) { console.error("Failed to load custom values", e); }
    finally { setLoaded(true); }
  }, [leadId]);

  useEffect(() => { loadCustomValues(); }, [loadCustomValues]);

  const setCustomValue = (key, val) => {
    setCustomValues(prev => ({ ...prev, [key]: val }));
  };

  const saveCustomValues = async () => {
    if (!leadId || Object.keys(customValues).length === 0) return;
    try {
      await fetch(`${API}/api/leads/${leadId}/custom-values`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(customValues)
      });
    } catch (e) { console.error("Failed to save custom values", e); }
  };

  return { customValues, setCustomValue, saveCustomValues, loaded };
}
